import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {delay} from 'rxjs/operators';

import {AgentInvite} from 'src/app/modals/invite-agent/model/AgentInvite.model';
import {environment} from 'src/environments/environment';
import {Agent} from '.';
import {Channel, ChannelHistory} from './model/channel.model';
import {CreateChannelRequest, MemberSearchResult} from './model/member.model';
import {PageResponse, Team, TeamDetail} from './model/team.model';
import {HistoryWalletBalance, WalletAgent} from './model/wallet.model';
import {AgentPixCreate, AgentPixResponse, InitPaymentResponse} from './model/agent-pix.model';

export type TermsOfUseDocumentType =
  | 'CLIENT_RSM_CAMPAIGNS'
  | 'CLIENT_RSM_DEDICATED'
  | 'CLIENT_RSM_POLITICS'
  | 'USER_COMMERCIAL_APP'
  | 'USER_VOLUNTEER_POLITICS_APP'
  | 'PRIVACY_POLICY_APP';

export interface TermsOfUse {
  id: string;
  title: string;
  documentType: TermsOfUseDocumentType;
  content: string;
  observation?: string | null;
  active: boolean;
  accepted?: boolean | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  acceptedAt?: string | null;
}

/** Situação do usuário perante o RSM Politics. */
/** Cor base do aplicativo definida pela empresa do agente. */
export interface AppTheme {
  /** BLUE | YELLOW | RED | GREEN | PINK | BLACK. */
  color?: string;
  hex: string;
  /** Tom escuro (barra inferior, botao pressionado). */
  shade?: string;
  /** Tom claro. */
  tint?: string;
  /** Cor do texto sobre a cor base, resolvida no servidor. */
  contrast?: string;
  /** Tom profundo (texto sobre fundo claro, sombras). Não é o shade. */
  deep?: string;
}

export interface PoliticsStatus {
  /** Tem vínculo com ao menos uma rede de apoio voluntário. */
  member?: boolean;
  /** Aceitou a versão VIGENTE do termo do apoiador voluntário. */
  termsAccepted?: boolean;
  /** Já aderiu, mas o termo mudou e a versão vigente ainda não foi aceita. */
  termsUpdatePending?: boolean;
  /** Vínculo E aceite: é o que libera o modo de apoio voluntário. */
  active?: boolean;
  companies?: { companyId?: string; contextId?: string; name?: string }[];
  quarantined?: boolean;
  quarantineEndDate?: string | null;
}

/** Resultado da saída da rede de apoio voluntário RSM Politics. */
export interface PoliticsOptOutResult {
  /** Bases das quais o vínculo foi removido (a coligação cai junto). */
  removedCompanies?: string[];
  /** Até quando o apoiador fica impedido de aderir a outra rede Politics. */
  quarantineEndDate?: string | null;
  /** false quando não havia termo ativo para registrar a revogação. */
  consentLogged?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AgentClientService {
  constructor(private http: HttpClient) { }

  public getByUserId(userId: string): Observable<Agent> {
    return this.http.get<Agent>(`${environment.agent.url}/agent/user/${userId}`);
  }

  public deleteUser(userId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${environment.agent.url}/agent/user/${userId}`);
  }

  /** Auto-exclusão LGPD: anonimiza o próprio cadastro (mantém histórico, bloqueia acesso). */
  public anonymizeSelf(userId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${environment.agent.url}/agent/user/${userId}/anonymize`);
  }

  // // TODO: Verificar onde é chamado
  // public save(data: Agent): Observable<Agent> {
  //   return this.http.post<Agent>(`${environment.agent.url}/agent/company`, data);
  // }

  /**
   * Saída da rede de apoio voluntário RSM Politics. A resposta traz de quais
   * bases o apoiador saiu e até quando fica em quarentena.
   */
  /**
   * Situação do usuário perante o RSM Politics: se é apoiador, se aceitou o termo
   * vigente, de quais bases participa e se está em quarentena.
   */
  public getPoliticsStatus(userId: string): Observable<PoliticsStatus> {
    return this.http.get<PoliticsStatus>(`${environment.agent.url}/agent/user/${userId}/politics/status`);
  }

  /** Cor base do app para a empresa do agente logado. Sem empresa, o azul padrão. */
  public getAppTheme(): Observable<AppTheme> {
    return this.http.get<AppTheme>(`${environment.agent.url}/agent/app-theme`);
  }

  public removePoliticsLink(userId: string): Observable<PoliticsOptOutResult> {
    return this.http.delete<PoliticsOptOutResult>(`${environment.agent.url}/agent/user/${userId}/politics-link`);
  }

  public agent(data: any): Observable<any> {
    return this.http.post<Agent>(`${environment.agent.url}/agent`, data);
  }

  public update(data: any): Observable<Agent> {
    return this.http.put<Agent>(`${environment.agent.url}/agent`, data);
  }

  public uploadProfileImage(userId: string, file: File): Observable<any> {
    // Mocking upload for now as the endpoint /agent/{userId}/profile-image returns 404
    // Replace with real endpoint when backend is ready. Example:
    // const formData = new FormData();
    // formData.append('file', file);
    // return this.http.post(`${environment.agent.url}/agent/${userId}/profile-image`, formData);
    return of({ success: true, url: URL.createObjectURL(file) }).pipe(delay(1500));
  }

  public invite(data: AgentInvite): Observable<AgentInvite> {
    return this.http.post<AgentInvite>(`${environment.agent.url}/invite`, data);
  }

  public getFieldTemplate(contextId: string): Observable<any> {
    return this.http.get<any>(`${environment.agent.url}/agent/user/company/${contextId}/field-template`);
  }

  public getCustomSections(userId: string): Observable<any[]> {
    return this.http.get<any[]>(`${environment.agent.url}/agent/user/${userId}/custom-sections`);
  }

  public saveCustomSections(userId: string, sections: any[]): Observable<any> {
    return this.http.put<any>(`${environment.agent.url}/agent/user/${userId}/custom-sections`, sections);
  }

  public getChannels(status = 'ACTIVE'): Observable<Channel[]> {
    return this.http.get<Channel[]>(`${environment.agent.url}/agent/channels/search`, {
      params: { status },
    });
  }

  public getChannelHistory(userId: string): Observable<ChannelHistory[]> {
    return this.http.get<ChannelHistory[]>(`${environment.agent.url}/agent/channels/history/${userId}`);
  }

  /**
   * Conversas ativas do ambiente informado. Sem o escopo, conversa de campanha
   * comercial aparecia dentro do RSM Politics.
   */
  public getActiveChannels(userId: string, scope?: 'COMMERCIAL' | 'POLITICS'): Observable<ChannelHistory[]> {
    const query = scope ? `?scope=${scope}` : '';
    return this.http.get<ChannelHistory[]>(`${environment.agent.url}/agent/channels/active/${userId}${query}`);
  }

  public createChannel(data: CreateChannelRequest): Observable<any> {
    return this.http.post(`${environment.agent.url}/agent/channels`, data);
  }

  public searchMembers(q: string, size = 10): Observable<MemberSearchResult[]> {
    return this.http.get<MemberSearchResult[]>(`${environment.agent.url}/agent/members/search`, {
      params: { q, size: size.toString() },
    });
  }

  public closeChannel(channelId: string): Observable<any> {
    return this.http.delete(`${environment.agent.url}/agent/channels/${channelId}`);
  }

  public getMyTeams(q?: string, page = 0, size = 20): Observable<PageResponse<Team>> {
    const params: Record<string, string> = { page: String(page), size: String(size) };
    if (q?.trim()) { params['q'] = q.trim(); }
    return this.http.get<PageResponse<Team>>(`${environment.agent.url}/teams/mine`, { params });
  }

  public getTeamDetail(id: string): Observable<TeamDetail> {
    return this.http.get<TeamDetail>(`${environment.agent.url}/teams/${id}`);
  }

  public getWallet(userId: string): Observable<WalletAgent> {
    const url = `${environment.agent.url}/agent/wallet-by-user/${userId}`;
    return this.http.get<WalletAgent>(url);
  }

  public getHistoryWalletBalance(userId: string, type?: string): Observable<HistoryWalletBalance[]> {
    let url = `${environment.agent.url}/agent/history-wallet-balance/${userId}`;
    if (type !== null && type !== undefined) {
      switch (type) {
        case 'entradas':
          url = url + `?type=Credit`;
          break;
        case 'saidas':
          url = url + `?type=Debt`;
          break;
      }
    }
    return this.http.get<HistoryWalletBalance[]>(url);
  }

  public getAllPix(): Observable<AgentPixResponse[]> {
    const url = `${environment.agent.url}/agent/pix`;
    return this.http.get<AgentPixResponse[]>(url);
  }

  public getPixData(): Observable<AgentPixResponse> {
    const url = `${environment.agent.url}/agent/pix/find/active`;
    return this.http.get<AgentPixResponse>(url);
  }

  public createPix(body: AgentPixCreate): Observable<AgentPixResponse> {
    const url = `${environment.agent.url}/agent/pix`;
    return this.http.post<AgentPixResponse>(url, body);
  }

  public confirmTransfer(body: any): Observable<InitPaymentResponse> {
    const url = `${environment.agent.url}/agent/pix/confirm/transfer`;
    return this.http.post<InitPaymentResponse>(url, body);
  }

  public getActiveTermsOfUse(documentType: TermsOfUseDocumentType): Observable<TermsOfUse> {
    return this.http.get<TermsOfUse>(`${environment.agent.url}/terms-of-use/active/${documentType}`);
  }

  public getPendingAppTermsOfUse(): Observable<TermsOfUse[]> {
    return this.http.get<TermsOfUse[]>(`${environment.agent.url}/terms-of-use/app/pending`);
  }

  public acceptActiveTermsOfUse(documentType: TermsOfUseDocumentType): Observable<TermsOfUse> {
    return this.http.post<TermsOfUse>(`${environment.agent.url}/terms-of-use/active/${documentType}/accept`, {});
  }
}
